export class PasswordlessAuthService {
  private baseURL: string;

  constructor(baseURL: string = '') {
    this.baseURL = baseURL;
  }

  async sendMagicLink(email: string): Promise<{ ok: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.baseURL}/auth/passwordless/magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const result = await response.json();
      return result;
    } catch (error) {
      return { ok: false, error: 'Network error' };
    }
  }

  async verifyMagicLink(token: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.baseURL}/auth/passwordless/magic-link/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token })
      });

      const result = await response.json();
      return result;
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  }

  async registerWebAuthn(email: string): Promise<{ ok: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.baseURL}/auth/passwordless/webauthn/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const result = await response.json();
      
      if (result.ok && result.options) {
        // Handle WebAuthn registration
        const credential = await navigator.credentials.create({
          publicKey: result.options
        }) as PublicKeyCredential;

        if (!credential) {
          return { ok: false, error: 'No credential created' };
        }

        // Verify the credential
        const verifyResponse = await fetch(`${this.baseURL}/auth/passwordless/webauthn/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            email, 
            credential: {
              id: credential.id,
              rawId: Array.from(new Uint8Array(credential.rawId)),
              response: {
                clientDataJSON: Array.from(new Uint8Array((credential.response as AuthenticatorAttestationResponse).clientDataJSON)),
                attestationObject: Array.from(new Uint8Array((credential.response as AuthenticatorAttestationResponse).attestationObject))
              },
              type: credential.type
            }
          })
        });

        return await verifyResponse.json();
      }

      return result;
    } catch (error) {
      return { ok: false, error: 'WebAuthn registration failed' };
    }
  }

  async authenticateWebAuthn(email: string): Promise<{ ok: boolean; error?: string }> {
    try {
      // Get authentication options
      const response = await fetch(`${this.baseURL}/auth/passwordless/webauthn/authenticate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const result = await response.json();
      
      if (result.ok && result.options) {
        // Handle WebAuthn authentication
        const assertion = await navigator.credentials.get({
          publicKey: result.options
        }) as PublicKeyCredential;

        if (!assertion) {
          return { ok: false, error: 'No assertion created' };
        }

        // Verify the assertion
        const verifyResponse = await fetch(`${this.baseURL}/auth/passwordless/webauthn/authenticate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            email,
            assertion: {
              id: assertion.id,
              rawId: Array.from(new Uint8Array(assertion.rawId)),
              response: {
                clientDataJSON: Array.from(new Uint8Array((assertion.response as AuthenticatorAssertionResponse).clientDataJSON)),
                authenticatorData: Array.from(new Uint8Array((assertion.response as AuthenticatorAssertionResponse).authenticatorData)),
                signature: Array.from(new Uint8Array((assertion.response as AuthenticatorAssertionResponse).signature)),
                userHandle: (assertion.response as AuthenticatorAssertionResponse).userHandle ? Array.from(new Uint8Array((assertion.response as AuthenticatorAssertionResponse).userHandle!)) : null
              },
              type: assertion.type
            }
          })
        });

        return await verifyResponse.json();
      }

      return result;
    } catch (error) {
      return { ok: false, error: 'WebAuthn authentication failed' };
    }
  }

  async checkSession(): Promise<{ ok: boolean; user?: any; error?: string }> {
    try {
      const response = await fetch(`${this.baseURL}/auth/passwordless/session`, {
        credentials: 'include'
      });
      return await response.json();
    } catch (error) {
      return { ok: false, error: 'Session check failed' };
    }
  }

  async logout(): Promise<{ ok: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.baseURL}/auth/passwordless/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      return await response.json();
    } catch (error) {
      return { ok: false, error: 'Logout failed' };
    }
  }
}

export const passwordlessAuth = new PasswordlessAuthService();
